import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Home,
  FolderOpen,
  Search,
  MessagesSquare,
  RotateCcw,
  Box,
  BarChart3,
  Settings,
  CreditCard,
  Users,
  PanelLeft,
  ShieldAlert,
  FlaskConical,
  X,
} from "lucide-react";
import { useEntitlement } from "@/contexts/EntitlementContext";
import { useFlag } from "@/contexts/FlagsContext";
import { useResponsive } from "@/contexts/ResponsiveContext";
import { useIsSuperadmin } from "@/hooks/useIsSuperadmin";
import { useRouter } from "@/hooks/useRouter";
import { cn } from "@/lib/utils/classnames";
import { NavigationRibbonItem } from "./NavigationRibbonItem";
import { UserPill } from "./UserPill";

const COLLAPSE_KEY = "specstory-ribbon-collapsed";

/**
 * Persistent left navigation. Collapsed ~52px icon-rail ↔ expanded ~220px.
 * Hidden on mobile, force-collapsed on tablet. Primary destinations: Home,
 * Projects, Analytics (Pro), Search/Ask, Resume (Pro), Skills (Pro); then Settings + Billing; then
 * the user pill. Pro destinations route through the upgrade prompt when locked.
 */
export function NavigationRibbon() {
  const { pathname } = useLocation();
  const router = useRouter();
  const { can } = useEntitlement();
  const showAnalytics = useFlag("analytics");
  const showResume = useFlag("resume");
  const showSkills = useFlag("skills");
  const showBilling = useFlag("billing");
  const showTeams = useFlag("teams");
  const isSuperadmin = useIsSuperadmin();

  const [saved, setSaved] = useState<boolean>(
    () => (typeof window !== "undefined" && window.localStorage.getItem(COLLAPSE_KEY) === "1"),
  );
  // On mobile the ribbon yields entirely to the bottom dock (AppShell renders
  // MobileBottomNav instead); between mobile and 1024px it's force-collapsed.
  const { isMobile, width } = useResponsive();
  const isTablet = !isMobile && width < 1024;
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const collapsed = isTablet || saved;

  const toggle = useCallback(() => {
    setSaved((prev) => {
      const next = !prev;
      window.localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      return next;
    });
  }, []);

  // "/" focuses search (expand first if needed).
  const focusSearch = useCallback(() => {
    if (collapsed && !isTablet) {
      setSaved(false);
      window.localStorage.setItem(COLLAPSE_KEY, "0");
      setTimeout(() => searchRef.current?.focus(), 210);
    } else {
      searchRef.current?.focus();
    }
  }, [collapsed, isTablet]);

  useEffect(() => {
    // The ribbon isn't rendered on mobile — don't claim the "/" hotkey there.
    if (isMobile) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "/" && !(e.target instanceof HTMLInputElement) && !(e.target instanceof HTMLTextAreaElement)) {
        e.preventDefault();
        focusSearch();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [focusSearch, isMobile]);

  const isActive = (prefix: string, exact = false) =>
    exact ? pathname === prefix : pathname === prefix || pathname.startsWith(prefix + "/") || pathname === prefix;

  if (isMobile) return null;

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    // Search is transient: only go to results when there's a query.
    if (!q) return;
    router.push(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <nav
      className="h-full flex-shrink-0 border-r border-border bg-card flex flex-col transition-[width] duration-200 overflow-x-clip"
      style={{ width: collapsed ? 52 : 220 }}
      aria-label="Main navigation"
    >
      {/* Brand + collapse toggle */}
      <div className={cn("flex items-center border-b border-border h-14", collapsed ? "justify-center" : "px-2.5 gap-2")}>
        {collapsed ? (
          <button onClick={toggle} className="flex items-center justify-center h-9 w-9 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted" aria-label="Expand sidebar" title="Expand sidebar">
            <PanelLeft size={18} />
          </button>
        ) : (
          <>
            <a href="/" className="flex items-center min-w-0 flex-1">
              <img
                src="/specstorylogo.svg"
                alt="SpecStory"
                className="h-5 w-auto dark:hidden"
              />
              <img
                src="/specstorylogo-inverted.svg"
                alt="SpecStory"
                className="hidden h-5 w-auto dark:block"
              />
            </a>
            {!isTablet && (
              <button onClick={toggle} className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted" title="Collapse sidebar">
                <PanelLeft size={16} />
              </button>
            )}
          </>
        )}
      </div>

      {/* Nav body */}
      <div className={cn("flex-1 overflow-y-auto overflow-x-clip", collapsed ? "px-1 py-2" : "p-2")}>
        {/* Search */}
        <div className="mb-2">
          {collapsed ? (
            <button onClick={focusSearch} title="Search" className="flex items-center justify-center h-9 w-9 mx-auto rounded-md text-muted-foreground hover:text-foreground hover:bg-muted">
              <Search size={18} />
            </button>
          ) : (
            <form onSubmit={submitSearch}>
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <input
                  ref={searchRef}
                  type="text"
                  placeholder="Search…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full h-9 pl-8 pr-7 text-xs bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-brand transition-colors"
                />
                {query && (
                  <button type="button" onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    <X size={12} />
                  </button>
                )}
              </div>
            </form>
          )}
        </div>

        {/* Primary */}
        <div className="space-y-1">
          <NavigationRibbonItem icon={Home} label="Home" href="/" active={isActive("/", true)} collapsed={collapsed} />
          <NavigationRibbonItem icon={FolderOpen} label="Projects" href="/projects" active={isActive("/projects")} collapsed={collapsed} />
          <NavigationRibbonItem icon={MessagesSquare} label="Chats" href="/chats" active={isActive("/chats")} collapsed={collapsed} />
          {/* Environment flags: hidden entirely where the surface isn't shipped yet. */}
          {/* Analytics navigates to /analytics for everyone (Pro and non-Pro).
              Non-Pro users see upgrade messaging + education on the page itself
              (AnalyticsGate) instead of a modal. Other Pro destinations still use
              proNav (modal-on-click when locked). */}
          {showAnalytics && (
            <NavigationRibbonItem
              icon={BarChart3}
              label="Analytics"
              href="/analytics"
              active={isActive("/analytics")}
              collapsed={collapsed}
              pro={!can("analytics")}
            />
          )}
          {/* Resume navigates to /resume for everyone (Pro and non-Pro). Non-Pro
              users see upgrade messaging at the top of the page instead of a modal —
              the page itself teases the capability. Other Pro destinations below still
              use proNav (modal-on-click when locked). */}
          {showResume && (
            <NavigationRibbonItem
              icon={RotateCcw}
              label="Resume"
              href="/resume"
              active={isActive("/resume")}
              collapsed={collapsed}
              pro={!can("resume")}
            />
          )}
          {/* Skills navigates to /skills for everyone (Pro and non-Pro).
              Non-Pro users see upgrade messaging + education on the page itself
              (SkillsGate) instead of a modal. */}
          {showSkills && (
            <NavigationRibbonItem
              icon={Box}
              label="Skills"
              href="/skills"
              active={isActive("/skills") || isActive("/skills/installed") || isActive("/skills/runs")}
              collapsed={collapsed}
              pro={!can("skills")}
            />
          )}
        </div>

        {/* Settings + billing */}
        <div className="my-2 mx-1 border-t border-border" />
        <div className="space-y-1">
          <NavigationRibbonItem icon={Settings} label="Settings" href="/settings" active={isActive("/settings") || isActive("/api-keys")} collapsed={collapsed} />
          {showTeams && <NavigationRibbonItem icon={Users} label="Teams" href="/teams" active={isActive("/teams")} collapsed={collapsed} pro={!can("teams")} />}
          {showBilling && <NavigationRibbonItem icon={CreditCard} label="Billing" href="/billing" active={isActive("/billing")} collapsed={collapsed} />}
        </div>

        {/* Superadmin — SpecStory staff only. */}
        {isSuperadmin && (
          <>
            <div className="my-2 mx-1 border-t border-border" />
            <div className="space-y-1">
              <NavigationRibbonItem icon={ShieldAlert} label="Admin" href="/admin/skills" active={isActive("/admin/skills") || isActive("/admin/analytics")} collapsed={collapsed} />
              {import.meta.env.MODE !== "production" && (
                <NavigationRibbonItem icon={FlaskConical} label="Script test" href="/admin/merge9-smoke" active={isActive("/admin/merge9-smoke")} collapsed={collapsed} />
              )}
            </div>
          </>
        )}
      </div>

      <UserPill collapsed={collapsed} />
    </nav>
  );
}
